import React, { useContext, useState, useEffect } from "react";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { FaBook } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import Alert from "../../UI/Alert/Alert";
import { NotifyContext } from "../../context/notifyContext";
import BookingService from "../../services/api/BookingService";
import { BookingDetails as BookingDetailsType } from "../../Types/Dashboard";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function BookingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);

  useBreadcrumbs([
    {
      label: "Sessions",
      icon: <FaBook />,
      is_link: true,
      href: "/app/bookings",
    },
    {
      label: id ?? "",
      is_link: false,
    },
    {
      label: "Détails",
      is_link: false,
    },
  ]);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<BookingDetailsType | null>(null);

  useEffect(() => {
    if (id) {
      fetchBookingDetails();
    }
  }, [id]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await BookingService.getBookingDetails(Number(id));
      setBooking(response.data.booking);
      // Debug: log survey session data
      if (response.data.booking.surveySession) {
        console.log("Survey Session:", response.data.booking.surveySession);
        console.log("Responses:", response.data.booking.surveySession.responses);
      }
    } catch (err: any) {
      setError(err?.message || "Erreur lors du chargement de la session");
      notify(err?.message || "Erreur lors du chargement de la session", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "dd MMM yyyy, HH:mm", { locale: fr });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="p-6">
        <Alert color="error">{error || "Session non trouvée"}</Alert>
        <button
          onClick={() => navigate("/app/bookings")}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Détails de la Session #{booking.id}</h1>
          <span
            className={`px-3 py-1 rounded text-sm font-medium ${getStatusColor(
              booking.status
            )}`}
          >
            {booking.status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Informations générales */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Informations générales</h2>
            <div className="space-y-3">
              <div>
                <span className="text-gray-600">Date de début:</span>
                <p className="font-medium">{formatDate(booking.start_time)}</p>
              </div>
              <div>
                <span className="text-gray-600">Date de fin:</span>
                <p className="font-medium">{formatDate(booking.end_time)}</p>
              </div>
              <div>
                <span className="text-gray-600">Méthode de communication:</span>
                <p className="font-medium capitalize">{booking.communication_method}</p>
              </div>
              {booking.meeting_link && (
                <div>
                  <span className="text-gray-600">Lien de réunion:</span>
                  <p className="font-medium">
                    <a
                      href={booking.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {booking.meeting_link}
                    </a>
                  </p>
                </div>
              )}
              <div>
                <span className="text-gray-600">Coût en crédits:</span>
                <p className="font-medium">{booking.credits_cost}</p>
              </div>
            </div>
          </div>

          {/* Contexte */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Contexte</h2>
            <p className="text-gray-700">{booking.context}</p>
          </div>
        </div>
      </div>

      {/* Étudiant */}
      {booking.student && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Étudiant</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-gray-600">Nom:</span>
              <p className="font-medium">
                {booking.student.first_name} {booking.student.last_name}
              </p>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <p className="font-medium">{booking.student.email}</p>
            </div>
            {booking.student.educational_system && (
              <div>
                <span className="text-gray-600">Système éducatif:</span>
                <p className="font-medium">{booking.student.educational_system.title}</p>
              </div>
            )}
            {booking.student.current_level && (
              <div>
                <span className="text-gray-600">Niveau actuel:</span>
                <p className="font-medium">{booking.student.current_level.title}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tuteur */}
      {booking.tutor && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Tuteur</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-gray-600">Nom:</span>
              <p className="font-medium">
                {booking.tutor.first_name} {booking.tutor.last_name}
              </p>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <p className="font-medium">{booking.tutor.email}</p>
            </div>
            {booking.tutor.levels && booking.tutor.levels.length > 0 && (
              <div className="md:col-span-2">
                <span className="text-gray-600">Niveaux enseignés:</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {booking.tutor.levels.map((level: any) => (
                    <span
                      key={level.id}
                      className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                    >
                      {level.title}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feedbacks */}
      {(booking.student_feedback || booking.tutor_feedback) && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Feedbacks</h2>
          <div className="space-y-4">
            {booking.student_feedback && (
              <div>
                <span className="text-gray-600 font-medium">Feedback étudiant:</span>
                <p className="mt-2 text-gray-700">{booking.student_feedback}</p>
                {booking.student_rating && (
                  <p className="mt-1 text-sm text-gray-500">
                    Note: {booking.student_rating}/5
                  </p>
                )}
              </div>
            )}
            {booking.tutor_feedback && (
              <div>
                <span className="text-gray-600 font-medium">Feedback tuteur:</span>
                <p className="mt-2 text-gray-700">{booking.tutor_feedback}</p>
                {booking.tutor_rating && (
                  <p className="mt-1 text-sm text-gray-500">
                    Note: {booking.tutor_rating}/5
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Survey Session */}
      {(booking.surveySession || (booking as any).survey_session) && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Session de sondage</h2>
          {(() => {
            const surveySession = booking.surveySession || (booking as any).survey_session;
            return (
              <div className="space-y-3">
                <div>
                  <span className="text-gray-600">Token:</span>
                  <p className="font-mono text-sm">
                    {surveySession.survey_token || surveySession.surveyToken}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Statut:</span>
                  <p className="font-medium">
                    {surveySession.submitted_at || surveySession.submittedAt
                      ? "Soumis"
                      : "En attente"}
                  </p>
                </div>
                {surveySession.responses &&
                  surveySession.responses.length > 0 && (
                    <div>
                      <span className="text-gray-600 font-medium">
                        Réponses:
                      </span>
                      <div className="mt-2 space-y-2">
                        {surveySession.responses.map((response: any) => {
                          // Question text (FR en priorité, sinon EN)
                          const questionText =
                            response.question?.questionTextFr ||
                            response.question?.question_text_fr ||
                            response.question?.questionTextEn ||
                            response.question?.question_text_en ||
                            "";

                          const type = response.question?.type as
                            | "rating"
                            | "text"
                            | "select"
                            | undefined;

                          // Answer values (camelCase ou snake_case)
                          const answerText =
                            response.answerText || response.answer_text || "";
                          const answerValueRaw =
                            response.answerValue ?? response.answer_value;
                          const ratingValue =
                            typeof answerValueRaw === "number"
                              ? answerValueRaw
                              : answerValueRaw != null
                              ? Number(answerValueRaw)
                              : null;

                          // Libellé pour les questions de type "select"
                          let selectLabel: string | null = null;
                          if (
                            type === "select" &&
                            response.question?.options &&
                            (answerText || answerValueRaw != null)
                          ) {
                            const optionValue = String(
                              answerText || answerValueRaw
                            );
                            const opt = response.question.options.find(
                              (o: any) => String(o.value) === optionValue
                            );
                            if (opt) {
                              selectLabel = opt.label_fr || opt.label_en || null;
                            }
                          }

                          return (
                            <div
                              key={response.id}
                              className="p-3 bg-gray-50 rounded"
                            >
                              <p className="font-medium text-sm mb-1">
                                {questionText}
                              </p>

                              {/* Affichage spécifique selon le type */}
                              {type === "rating" && ratingValue != null && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">
                                    {ratingValue.toFixed(1)}
                                  </span>
                                  <div className="flex">
                                    {Array.from({ length: 5 }, (_, index) => {
                                      const starValue = index + 1;
                                      const isFilled =
                                        starValue <= ratingValue;
                                      return (
                                        <svg
                                          key={starValue}
                                          className={`w-4 h-4 ${
                                            isFilled
                                              ? "text-yellow-400 fill-current"
                                              : "text-gray-300"
                                          }`}
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {type === "select" && selectLabel && (
                                <span className="inline-flex items-center px-2 py-1 mt-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                                  {selectLabel}
                                </span>
                              )}

                              {type === "text" && answerText && (
                                <p className="text-gray-700 mt-1">
                                  {answerText}
                                </p>
                              )}

                              {/* Fallback si pas de type clair */}
                              {!type && (answerText || ratingValue != null) && (
                                <p className="text-gray-700 mt-1">
                                  {answerText ||
                                    (ratingValue != null
                                      ? ratingValue.toString()
                                      : "")}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
              </div>
            );
          })()}
        </div>
      )}

      <div className="flex gap-4">
        <button
          onClick={() => navigate("/app/bookings")}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          Retour à la liste
        </button>
      </div>
    </div>
  );
}
